/**
 * 预订、入住优惠券
 * 需要解锁券 单独拎出来
 */
import React, { useImperativeHandle, useRef, useState } from 'react';
import { Modal, Spin, Tabs, Form } from 'antd';
import intl from 'react-intl-universal';

import {
  fetchAvailableCouponList,
  fetchCouponCalculate,
  fetchDesensitizedInfoByWyuid,
} from '@/api/guest';
import { CouponStatus, OccType, RESERVATION_SCENE, SourceChannelType } from '@/config/constant';
import styles from '@/components/SelectDiscountCoupon/index.module.scss';

import WYCouponList, { ICouponItem } from './WYCouponList';
import XZCouponList from './XZCouponList';

interface ISelectorConfig {
  wyuid: string; // 用户id
  amount: string; // 房价、以分为单位
  occType: OccType; // 入住类型
  changedCouponId: string[]; // 当前选择的券
  sceneType: RESERVATION_SCENE; // 用券场景
  lockCouponList?: string[]; // 页面锁定的券 包含当前选择的券
  unlockCouponList?: string[]; // 解锁的券 优先级高于后台数据
  // value: 选中的券id record选中的券信息
  callback?: (value: string[], record?: Partial<ICouponItem>[]) => void;
  channelType: SourceChannelType; // 客源渠道
  businessDate?: string; // YYYY-MM-DD
  sortType?: 1 | 2; // 排序类型
}

export interface CouponSelectorRef {
  openModal: (value: ISelectorConfig) => void;
  closeModal: () => void;
}

interface IProps {
  myRef?: React.Ref<CouponSelectorRef>;
}

const SelectDiscountCoupon: React.FC<IProps> = ({ myRef }) => {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [isModalLoading, setModalLoading] = useState<boolean>(false);
  const [manualCouponCount, setManualCouponCount] = useState(0); // 手工券数量
  const [couponList, setCouponList] = useState<ICouponItem[]>([]);
  const [customerInfo, setCustomerInfo] = useState<
    Partial<{
      name: string;
      certNo: string;
      mobile: string;
    }>
  >({});
  // 选券入参
  const selectorConfigRef = useRef<ISelectorConfig>();

  // 获取配置
  const getSelectorConfig = () => {
    return selectorConfigRef.current || ({} as ISelectorConfig);
  };

  // 获取网鱼券列表
  const getWYCouponList: () => Promise<ICouponItem[]> = async () => {
    const { wyuid, occType, amount, businessDate, sortType } = getSelectorConfig();
    try {
      const params = { wyId: wyuid, occType, amount, businessDate, sortType };
      const res = await fetchAvailableCouponList(params);
      return res.data || [];
    } catch (e) {
      //
    }
    return [];
  };

  // 格式化网鱼券信息
  const updateWYCouponList = (data: {
    list: ICouponItem[];
    selectCouponList: string[];
    lockCouponList: string[];
    unlockCouponList: string[];
  }) => {
    const { list, selectCouponList, lockCouponList, unlockCouponList } = data;
    const unlockCouponMap = {};
    const lockCouponMap = {};
    const selectCouponMap = {};
    (unlockCouponList || []).forEach((couponNo) => {
      unlockCouponMap[couponNo] = true;
    });
    (lockCouponList || []).forEach((couponNo) => {
      lockCouponMap[couponNo] = true;
    });
    (selectCouponList || []).forEach((couponNo) => {
      selectCouponMap[couponNo] = true;
    });
    setCouponList(
      list.map((item) => {
        const { status, couponNo } = item;
        // 当前券是否可选
        // 券状态为有效 或者 券状态为锁定但在解锁列表里
        const checkable =
          status === CouponStatus.Effective ||
          (status === CouponStatus.Locked && unlockCouponMap[couponNo]);
        // 当前券是否为选中状态
        const isChecked = selectCouponMap[couponNo];
        // 当前券是否禁用
        // 在锁定列表单当前没选中 或者 不可选
        const isDisabled = (lockCouponMap[couponNo] && !isChecked) || !checkable;
        return {
          ...item,
          // 券可选时 状态重置为有效
          status: checkable ? CouponStatus.Effective : item.status,
          isChecked,
          isDisabled,
        };
      }),
    );
  };

  // 初始化网鱼券列表
  // const initWYCouponList = async () => {
  //   setModalLoading(true);
  //   const listData = await getWYCouponList();
  //   const { changedCouponId, lockCouponList, unlockCouponList } = getSelectorConfig();
  //   updateWYCouponList({
  //     list: listData,
  //     selectCouponList: changedCouponId,
  //     lockCouponList,
  //     unlockCouponList,
  //   });
  //   setModalLoading(false);
  // };

  // 刷新网鱼券列表
  const refreshWYCouponList = async () => {
    setModalLoading(true);
    const listData = await getWYCouponList();
    const { changedCouponId, lockCouponList, unlockCouponList } = getSelectorConfig();
    // TODO 需不需要特殊处理
    updateWYCouponList({
      list: listData,
      selectCouponList: changedCouponId,
      lockCouponList,
      unlockCouponList,
    });
    setModalLoading(false);
  };

  // 获取账户信息
  const getCustomerInfo = (id: string) => {
    fetchDesensitizedInfoByWyuid({ wyuid: id }).then((res) => {
      setCustomerInfo(res.data || {});
    });
  };

  // 关闭弹框
  const closeModal = () => {
    setModalVisible(false);
  };

  // ref
  useImperativeHandle(
    myRef,
    () => ({
      openModal: (config) => {
        selectorConfigRef.current = config;
        setCustomerInfo({});
        setManualCouponCount(0);
        // initWYCouponList();
        getCustomerInfo(config.wyuid);
        setModalVisible(true);
      },
      closeModal,
    }),
    [],
  );

  // 计算网鱼券实际抵扣金额
  const calcCouponValue: (coupon: ICouponItem) => Promise<ICouponItem[]> = async (coupon) => {
    const { wyuid, occType, amount, businessDate } = selectorConfigRef.current;
    const params = {
      wyuid,
      occType,
      roomPrice: amount,
      lockCouponNo: coupon.couponNo,
      businessDate,
    };
    setModalLoading(true);
    const res = await fetchCouponCalculate(params);
    if (res && res.data) {
      return [
        {
          ...coupon,
          couponFaceValue: res.data.couponAmount || 0,
        },
      ];
    }
    return [];
  };

  // 确认操作
  const handleOk = () => {
    Promise.resolve()
      .then(() => {
        // 筛选选中的券
        const recordList = couponList.filter((item) => item.isChecked) || [];
        return recordList;
      })
      .then((recordList) => {
        // 只取第一个
        const targetCoupon = recordList[0];
        if (targetCoupon) {
          return calcCouponValue(targetCoupon);
        }
        return [];
      })
      .then((recordList) => {
        const callbackHandler = getSelectorConfig().callback;
        if (typeof callbackHandler === 'function') {
          callbackHandler(
            recordList.map((item) => item.couponNo),
            recordList,
          );
        }
        closeModal();
      })
      .catch(() => {
        //
        setModalLoading(false);
      });
  };

  // 网鱼券选中回调
  const handleSelect = (couponNo) => {
    setCouponList(
      couponList.map((item) => {
        return {
          ...item,
          isChecked: item.isChecked ? false : item.couponNo === couponNo,
        };
      }),
    );
  };

  // 修改排序
  const handleSortTypeChange = (sortType) => {
    selectorConfigRef.current.sortType = sortType;
    refreshWYCouponList();
  };

  const selectorConfigValue = getSelectorConfig();

  return (
    <Modal
      wrapClassName={styles.container}
      title={intl.get('reservation_cmp_coupon_selector_title')}
      destroyOnClose
      maskClosable={false}
      open={modalVisible}
      onOk={handleOk}
      confirmLoading={isModalLoading}
      onCancel={closeModal}
    >
      <div className={styles.info}>
        <Form.Item label={intl.get('reservation_cmp_coupon_selector_name_label')}>
          {customerInfo?.name}
        </Form.Item>
        <Form.Item label={intl.get('app_reservation_cert_number')}>
          {customerInfo?.certNo}
        </Form.Item>
        {/* <Form.Item label={intl.get('app_reservation_phone_number')}>
          {customerInfo?.mobile}
        </Form.Item> */}
      </div>
      <Spin spinning={isModalLoading}>
        <Tabs
          type="card"
          items={[
            {
              key: 'WY',
              label: intl.get('reservation_cmp_coupon_selector_wy_coupon_count', {
                count: couponList.filter((item) => !item.isDisabled).length,
              }),
              forceRender: true,
              children: (
                <WYCouponList
                  guestInfo={customerInfo}
                  handleSelect={handleSelect}
                  couponList={couponList}
                  channelType={selectorConfigValue.channelType}
                  refreshList={refreshWYCouponList}
                  onSortTypeChange={handleSortTypeChange}
                />
              ),
            },
            {
              key: 'XZ',
              label: intl.get('reservation_cmp_coupon_selector_xz_coupon_count', {
                count: manualCouponCount,
              }),
              forceRender: true,
              children: (
                <XZCouponList
                  wyuid={selectorConfigValue.wyuid}
                  sceneType={selectorConfigValue.sceneType}
                  onListUpdate={(list) => {
                    setManualCouponCount(list.length);
                  }}
                />
              ),
            },
          ]}
        />
      </Spin>
    </Modal>
  );
};

export default React.forwardRef<CouponSelectorRef>((props, ref) => (
  <SelectDiscountCoupon {...props} myRef={ref} />
));
